import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { getQueryParams } from "@/utils/httpRequest";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics, sendCollectionToAnalytics } from "@/utils/analytics";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";
import { showNotification } from "@/redux/actions/notification";
import { LIST_SERVICE_TAG } from "@/shared/store/service/commonServiceTags";
import { lenderHubDealService } from "@/features/LenderHub/Deal/store/services/lenderHubDealService";

export const lenderHubChatService = createApi({
  reducerPath: "lenderHubChatApi",
  tagTypes: ["LenderHubMessage"],
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/capworks/",
    credentials: "include",
  }),
  endpoints: builder => ({
    getMessages: builder.infiniteQuery({
      query: (queryArg, api = {}) => {
        // Handle the nested queryArg structure from RTK Query infiniteQuery
        const actualArgs = queryArg.queryArg || queryArg;

        // RTK Query passes pageParam in the top-level queryArg for subsequent calls
        const pageParam = queryArg.pageParam || api.pageParam || 1;

        const { dealId, ...params } = actualArgs;

        // Remove pageParam and queryArg from params to avoid URL pollution
        const cleanParams = { ...params };
        delete cleanParams.pageParam;
        delete cleanParams.queryArg;

        const queryString = getQueryParams({ ...cleanParams, page: pageParam });
        return `deals/${dealId}/messages?${queryString}`;
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage) {
            return undefined;
          }

          // Use lastPageParam instead of API's pageNumber since API seems to always return pageNumber: 1
          const currentPage = lastPageParam || lastPage.pageNumber || 1;
          const { totalPages } = lastPage;

          if (!totalPages) {
            return undefined;
          }

          // Stop if we've reached the total pages or if the last page has no items
          if (currentPage >= totalPages || !lastPage.items?.length) {
            return undefined;
          }

          return currentPage + 1;
        },
      },
      providesTags: result =>
        result
          ? [
              ...(result.pages?.flatMap(
                page =>
                  page.items?.map(({ lenderBoardMessageId }) => ({
                    type: "LenderHubMessage",
                    id: lenderBoardMessageId,
                  })) || [],
              ) || []),
              { type: "LenderHubMessage", id: LIST_SERVICE_TAG },
            ]
          : [{ type: "LenderHubMessage", id: LIST_SERVICE_TAG }],
    }),

    createMessage: builder.mutation({
      query: ({ dealId, ...body }) => ({
        url: `deals/${dealId}/messages`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [{ type: "LenderHubMessage", id: LIST_SERVICE_TAG }],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_HUB_MESSAGE_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Message sent successfully",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderHubDealService.util.invalidateTags([{ type: "LenderHubDeal", id: queryArgs.dealId }]));
        } catch (error) {
          Bugsnag.notify(error);
        }
      },
    }),

    updateMessage: builder.mutation({
      query: ({ dealId, lenderHubMessageId, ...body }) => ({
        url: `deals/${dealId}/messages/${lenderHubMessageId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { lenderHubMessageId }) => [
        { type: "LenderHubMessage", id: lenderHubMessageId },
        { type: "LenderHubMessage", id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_HUB_MESSAGE_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Message updated successfully",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (error) {
          Bugsnag.notify(error);
        }
      },
    }),

    deleteMessage: builder.mutation({
      query: ({ dealId, lenderHubMessageId }) => ({
        url: `deals/${dealId}/messages/${lenderHubMessageId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { lenderHubMessageId }) => [
        { type: "LenderHubMessage", id: lenderHubMessageId },
        { type: "LenderHubMessage", id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_MESSAGE_DELETED, queryArgs);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Message deleted successfully",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
          dispatch(lenderHubDealService.util.invalidateTags([{ type: "LenderHubDeal", id: queryArgs.dealId }]));
        } catch (error) {
          Bugsnag.notify(error);
          dispatch(setProcessingState(false));
        }
      },
    }),
  }),
});

export const {
  useCreateMessageMutation,
  useDeleteMessageMutation,
  useGetMessagesInfiniteQuery,
  useUpdateMessageMutation,
} = lenderHubChatService;
